import React from "react";
import { FiMapPin } from "react-icons/fi";
import Logo from "../styles/Black-SVG-Landscape.svg";
import ChrisPhoto from "../candidates/MichelleTrent.jpg";



export default function HeadOfPartnershipsMichelleTrent() {
  const candidateProfileCopy = {
    name: "Michelle",
    location: "Oakland Township, MI",
    compensation: {
      range: "$180K base, $200K+ OTE target",
    },
    availability: "Available within 1 to 2 weeks of offer",
    loom_video_link: "https://fathom.video/share/acE3DJZUtoyFzk4S4G9FD56bvwjw79NR",
    tools: [
      {
        category: "Provider & Partnership Development",
        items: [
          "Physician and clinic partnership building across 800 plus clinics",
          "Joint ventures, acquisitions, and CMS aligned medical director agreements",
          "Health system and provider group relationship management",
          "Gatekeeper to decision maker access strategy",
        ],
      },
      {
        category: "Revenue & Growth Strategy",
        items: [
          "Data driven territory and opportunity prioritization",
          "Network development and provider practice expansion",
          "Pricing negotiation and end to end deal implementation",
          "CRM optimization, sales operations, and performance dashboards",
        ],
      },
      {
        category: "Team Leadership & Operations",
        items: [
          "Led teams of 15 to 20 managers and analysts",
          "Coaching and accountability for outbound and engagement teams",
          "Servant leadership with consistently high engagement scores",
          "Remote and national team management",
        ],
      },
      {
        category: "Healthcare & Regulatory",
        items: [
          "CMS, Medicare Advantage, MSSP, and value based care models",
          "Nephrology and kidney care provider partnerships",
          "Policy and procedure writing in regulated environments",
          "Compliance aware clinical stakeholder engagement",
        ],
      },
    ],
    soft_skills: [
      "15 plus years of healthcare partnership and provider engagement leadership",
      "National physician partnership leadership across 200 plus relationships and 800 plus clinics",
      "Led teams of 15 to 20 managers and analysts with high engagement scores",
      "Deep CMS, value based care, and healthcare compliance fluency",
      "Data first prioritization paired with patient, relationship driven selling",
      "Recognized for top business development performance, including a Highest ROI award",
    ],
    about_candidate:
      "Michelle is a healthcare executive who has built and led provider and physician partnerships for some of the largest care organizations in the country, including DaVita, Fresenius Medical Care, CVS Health, and Aledade. She has led all physician partnerships nationally across 200 plus relationships and 800 plus clinics, completed a $60M acquisition recognized as the highest valuation deal in her portfolio, and contributed to more than $190M in shared savings through provider performance. Her core strength is the exact motion this role depends on: winning the trust of clinical decision makers and turning that trust into durable, revenue generating partnerships.",
    core_strengths: [
      {
        headline: "Led physician partnerships at national scale",
        description:
          "At Fresenius Medical Care, Michelle was the national lead for all physician partnerships, spanning more than 200 physician relationships, 800 plus clinics, and 60,000 plus patients.",
      },
      {
        headline: "Creative dealmaker when the obvious path is closed",
        description:
          "When a hospital CEO declined to sell several clinics, Michelle structured a new non competing clinic with a right of first refusal on the others, then grew the relationship across the region.",
      },
      {
        headline: "Relationships that hold under pressure",
        description:
          "When a dialysis chain pulled her product from its formulary, the prescribing provider kept using it purely because of the relationship she had built, and she retained one hundred percent of her business.",
      },
      {
        headline: "Proven team leader in regulated healthcare",
        description:
          "Michelle has led teams of 15 to 20 managers and analysts across multiple organizations, with a servant leader style and consistently high engagement scores.",
      },
    ],
    relevant_experience: [
      {
        headline: "Senior Director, National Value Based Care Programs | Fresenius Medical Care | 2017 to 2021",
        bullets: [
          "Served as national lead for all physician partnerships across 200 plus provider relationships and 800 plus clinics",
          "Led and scaled a team of 15 program managers with full accountability for engagement strategy",
          "Contributed to more than $190M in shared savings through provider performance improvement",
          "Wrote policy and procedure and operated inside value based risk programs in a heavily regulated environment",
        ],
      },
      {
        headline: "Director, Business Development | DaVita | 2010 to 2015",
        bullets: [
          "Led multi state partner development across provider, hospital, and health system relationships in a five state region",
          "Completed multiple acquisitions including a $60M deal, the highest valuation in the portfolio",
          "Negotiated joint ventures, service line agreements, and CMS aligned medical director agreements",
          "Structured a creative new clinic partnership with a right of first refusal when a direct sale was declined",
          "Earned Highest ROI, Service Excellence, and Shining Star recognition for top business development performance",
        ],
      },
      {
        headline: "Lead Director, Value Based Care Client Success | CVS Health, Kidney Care | 2021 to 2022",
        bullets: [
          "Led and developed a team of 20 managers and analysts across CMS and Medicare Advantage partnerships",
          "Set performance metrics and dashboards and communicated results to senior stakeholders",
          "Partnered with payer and provider leadership to drive program growth and measurable outcomes",
        ],
      },
      {
        headline: "Senior Director, Network Development and Growth | Aledade | 2024 to 2025",
        bullets: [
          "Directed physician practice expansion and partner engagement across new and existing markets",
          "Led a team of five managers driving provider growth and pipeline performance",
          "Oversaw CRM optimization, sales operations, and partner engagement dashboards",
        ],
      },
      {
        headline: "Senior Director, Value Based Care Provider Engagement | Zing Health | 2025 to Present",
        bullets: [
          "Leads enterprise partner engagement for Medicare Advantage and C SNP populations",
          "Runs provider engagement, onboarding, quality incentive design, and performance improvement",
          "Works cross functionally with actuarial, finance, quality, and member experience leadership",
        ],
      },
    ],
  };

const technicalAssessment = {
  meta: {
    role: "Head of Healthcare Partnerships",
    interviewDate: "June 9, 2026",
    analyst: "AI Architechs Senior Hiring Analysis",
  },

  snapshot:
    "Michelle is a strong fit for the heart of this role: winning over doctors and clinic leaders and turning those relationships into lasting partnerships. She has done this at a national scale for more than 15 years, gets creative when the obvious path is blocked, and is very comfortable in regulated healthcare. Two things to know going in: she has not worked in fertility or women's health specifically, so that is an area she would ramp into, and she leans on relationships more than on building a brand new outreach system from scratch. Her pay target sits at the top of the range and she framed the move as lateral, so drive and pay are worth a quick conversation.",

  scorecard: [
    {
      dimension: "Partnership and Referral Channel Development",
      score: 9,
      rationale:
        "Led every physician partnership nationally at Fresenius across 200 plus relationships and 800 plus clinics, plus acquisitions and joint ventures at DaVita. Her best story, building a new clinic with first rights to buy the others when a sale fell through, shows a clear path from first meeting to a growing partnership.",
    },
    {
      dimension: "Building From Zero and Pipeline Construction",
      score: 8,
      rationale:
        "Her CV shows she has repeatedly entered and grown new markets, from a five state region at DaVita to new provider markets at Aledade, so she clearly knows how to build momentum from a standing start. In the interview she described this more through relationships and access than a formal pipeline, so the system building side is a touch lighter than the market entry track record.",
    },
    {
      dimension: "Revenue and Commercial Thinking",
      score: 8,
      rationale:
        "Clearly data driven, talking about revenue trends, patient volume, provider value, and which levers move the number. She framed it around understanding the business first and following the data, which is exactly the commercial instinct this role needs.",
    },
    {
      dimension: "Partner Conversion and Lifecycle Management",
      score: 8,
      rationale:
        "Talked about next steps, removing obstacles, and staying close after the deal, and her story of keeping all of a client's business through one loyal doctor shows she thinks well beyond the close. Strong on long term relationship management, with a lighter touch on the step by step onboarding process.",
    },
    {
      dimension: "SDR and Sales Team Leadership",
      score: 8,
      rationale:
        "Led teams of 15 to 20 plus a smaller outbound team, with real coaching examples like spotting why a rep was underperforming and resetting their approach. Strong on accountability, feedback, and engagement.",
    },
    {
      dimension: "Regulated Healthcare Experience and Compliance",
      score: 9,
      rationale:
        "Has spent her whole career inside regulated healthcare, written policies, and worked hand in hand with compliance. She clearly understands this is a slow, trust based process rather than a fast sales cycle.",
    },
    {
      dimension: "Fertility and Women's Health Vertical Exposure",
      score: 6,
      rationale:
        "No direct fertility or women's health experience, though she drew the comparison to her own field herself and the provider relationships transfer closely. Credit for strong, related experience, with this as her clearest area to ramp into.",
    },
    {
      dimension: "Ambition and Drive",
      score: 7,
      rationale:
        "She has owned real growth targets and major deals, not just supported them, which is a strong ownership signal. The one thing to probe is that she framed this as a lateral move and is not chasing a raise, coming across as steady, so it is worth confirming her hunger for the build.",
    },
  ],

  strengths: [
    {
      headline: "Deeply experienced in the core of the role",
      description:
        "Michelle has spent years doing exactly what this job centers on, building trust with doctors and clinic leaders and turning it into partnerships that last and grow.",
    },
    {
      headline: "Proven, not just promised",
      description:
        "Her relationship loyalty and creative dealmaking come with specific stories and real outcomes behind them, not general claims.",
    },
    {
      headline: "Safe in a regulated world",
      description:
        "She has worked inside healthcare compliance for years, so there is no learning curve on moving carefully and building trust the right way.",
    },
    {
      headline: "A leader who builds engaged teams",
      description:
        "She has led national teams of 15 to 20 with consistently high engagement, coaching reps and holding them accountable, which matters for someone who will run the outreach team.",
    },
  ],

  flags: [
    {
      headline: "Fertility is new ground",
      description:
        "Her experience is in kidney care and value based programs, not fertility or women's health. The relationships work much the same way, but she will need time to learn the specific patient journey and providers.",
    },
    {
      headline: "Relationship first, system second",
      description:
        "She builds through relationships more than through a structured outreach engine, even though her track record shows she can enter and grow new markets. Worth confirming she will also set up the repeatable system this build needs.",
    },
    {
      headline: "Confirm drive and pay",
      description:
        "She framed this as a lateral move and is not looking for a raise, so it is worth testing her hunger for the build. Her target is at the top of the range, so a quick alignment on pay makes sense.",
    },
  ],

  when_to_hire: [
    "You want someone who has spent years doing the core of this role, building and growing partnerships with doctors and clinic leaders, and you value a proven track record.",
    "Long term, trust based relationships matter more to you than aggressive selling, and you want partners who stay for years.",
    "Care and compliance in a regulated space are important, and you want someone who already knows how to move carefully and build trust the right way.",
    "You want a steady, communicative team leader who coaches reps, holds them accountable, and keeps engagement high.",
  ],

  when_to_be_cautious: [
    "You need someone who already knows fertility and women's health and can activate those specific relationships from day one, since this is the area she would ramp into.",
    "You want a high energy builder who will design the outreach system and pipeline from scratch, since she leans on relationships more than on standing up a new system.",
    "You are optimizing above all for hunger and ambition, since she presented as steady and framed the move as a lateral one.",
  ],

  bottom_line:
    "Michelle brings a strong, proven record of building lasting partnerships with doctors and clinics, backed by deep experience in regulated healthcare. The open questions are her ramp into fertility and women's health, whether she will build the outreach system as readily as she builds relationships, and her drive, since she framed this as a lateral move. All worth a direct conversation, and on the core of the role she is well equipped.",
};

  const ScorePill = ({ score }) => {
    let bg = "#f97316";
    if (score >= 8) bg = "#22c55e";
    else if (score >= 6) bg = "#36D1FF";
    return (
      <span
        style={{ backgroundColor: bg }}
        className="inline-block text-white text-xs font-bold px-3 py-1 rounded-full"
      >
        {score} / 10
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[#01014C] text-white flex flex-col items-center py-10 px-4">

      {/* ===================== PAGE 1 ===================== */}
      <div className="w-full max-w-6xl bg-[#FFFDFB] rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row relative">

        {/* LEFT COLUMN */}
        <aside className="relative md:w-1/3 bg-gradient-to-b from-[#01014C] via-[#0A0A80] to-[#36D1FF] p-8 flex flex-col items-center text-center overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-[#36D1FF]/30 to-transparent"></div>

          {/* Avatar placeholder */}
          <div className="relative mb-4">
            <div className="absolute inset-0 rounded-full bg-[#36D1FF]/20 blur-2xl"></div>
            <div className="relative w-36 h-36 rounded-full border-4 border-[#36D1FF] shadow-lg overflow-hidden">
              <img src={ChrisPhoto} alt="Chris Weston" className="w-full h-full object-cover" />
            </div>
          </div>

          {/* Name */}
          <h1 className="text-2xl font-semibold mb-1 text-[#FFFDFB]">
            {candidateProfileCopy.name}
          </h1>

          {/* Location */}
          <div className="flex items-center gap-2 text-[#E0F7FF] text-sm mb-6 opacity-90">
            <FiMapPin className="text-[#36D1FF]" />
            <span>{candidateProfileCopy.location}</span>
          </div>

          {/* Compensation */}
          <div className="bg-[#01014C] border border-[#36D1FF]/50 rounded-xl p-4 w-full text-left mb-4">
            <p className="text-[#B3E9FF] text-sm font-semibold uppercase mb-1">
              Compensation
            </p>
            <p className="text-[#36D1FF] text-lg font-bold">
              {candidateProfileCopy.compensation.range}
            </p>
          </div>

          {/* Availability */}
          <div className="bg-[#01014C] border border-[#36D1FF]/30 rounded-xl p-4 w-full text-left mb-6">
            <p className="text-[#E0F7FF] text-sm">
              {candidateProfileCopy.availability}
            </p>
          </div>

          {/* Watch Interview Button */}
          <a
            href={candidateProfileCopy.loom_video_link}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full mb-6 flex items-center justify-center gap-2 bg-gradient-to-r from-[#36D1FF] to-[#0099CC] hover:from-[#5DDDFF] hover:to-[#00AADD] text-[#01014C] font-bold text-sm py-3 px-6 rounded-xl transition-all duration-200 shadow-lg shadow-[#36D1FF]/20 hover:shadow-[#36D1FF]/40"
          >
            ▶ Watch Interview
          </a>

          {/* Tools */}
          <div className="w-full mt-4 mb-6 text-left">
            <h3 className="text-sm font-semibold mb-3 text-[#B3E9FF] uppercase">
              Tools & Capabilities
            </h3>
            {candidateProfileCopy.tools.map((group, i) => (
              <div key={i} className="mb-4">
                <p className="text-xs font-semibold text-[#36D1FF] mb-1 uppercase tracking-wide">
                  {group.category}
                </p>
                <ul className="space-y-1">
                  {group.items.map((tool, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-2 text-xs text-[#E0F7FF]/90 leading-snug"
                    >
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-[#36D1FF] shrink-0" />
                      {tool}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Soft Skills */}
          <div className="w-full mb-6 text-left">
            <h3 className="text-sm font-semibold mb-2 text-[#B3E9FF] uppercase">
              Key Profile Highlights
            </h3>
            <ul className="list-disc pl-5 space-y-1 text-[#E0F7FF] text-sm">
              {candidateProfileCopy.soft_skills.map((skill, i) => (
                <li key={i}>{skill}</li>
              ))}
            </ul>
          </div>

          {/* Logo placeholder */}
          <div className="w-full mt-8 flex justify-center">
            <img src={Logo} alt="AI Architechs" className="h-16 opacity-80" />
          </div>
        </aside>

        {/* RIGHT COLUMN */}
        <main className="flex-1 p-10 text-[#01014C] space-y-10">
          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              About this Candidate
            </h2>
            <p className="text-gray-700 leading-relaxed whitespace-pre-line">
              {candidateProfileCopy.about_candidate}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              Core Strengths
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {candidateProfileCopy.core_strengths.map((item, i) => (
                <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-5">
                  <p className="font-semibold mb-1">{item.headline}</p>
                  <p className="text-sm text-gray-700">{item.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3 border-b border-[#36D1FF]/40 pb-1">
              Relevant Experience
            </h2>
            <div className="space-y-6">
              {candidateProfileCopy.relevant_experience.map((exp, i) => (
                <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-5">
                  <p className="font-semibold text-[#EB5D2A] mb-2">{exp.headline}</p>
                  <ul className="list-disc pl-6 space-y-1 text-sm text-gray-700">
                    {exp.bullets.map((b, j) => (
                      <li key={j}>{b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>

      {/* ===================== PAGE BREAK BAND ===================== */}
      <div className="w-full py-10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="h-10 rounded-2xl bg-gradient-to-r from-[#01014C] via-[#0A0A80] to-[#36D1FF] opacity-70 shadow-[0_0_40px_#36D1FF20]" />
        </div>
      </div>

      {/* ===================== PAGE 2 ===================== */}
      <div className="w-full max-w-6xl bg-[#FFFDFB] rounded-3xl overflow-hidden shadow-2xl pb-2">
        <main className="p-10 text-[#01014C]">
          <div className="border border-[#36D1FF]/25 rounded-2xl p-6 bg-white shadow-[0_0_12px_#36D1FF15]">

            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-2 mb-6">
              <h2 className="text-2xl font-semibold text-[#01014C]">
                Technical Analysis
              </h2>
              <div className="flex flex-col items-start md:items-end gap-1 text-xs text-gray-400">
                <span>{technicalAssessment.meta.role}</span>
                <span>Interview: {technicalAssessment.meta.interviewDate}</span>
                <span>{technicalAssessment.meta.analyst}</span>
              </div>
            </div>

            {/* Snapshot */}
            <div className="border border-[#36D1FF]/20 rounded-xl p-5 bg-[#36D1FF]/5 mb-6">
              <p className="text-base font-bold text-[#01014C] mb-2">Executive Fit Snapshot</p>
              <p className="text-gray-700 text-base leading-relaxed whitespace-pre-line">
                {technicalAssessment.snapshot}
              </p>
            </div>

            {/* Strengths & Flags */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ✅ Strengths
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.strengths.map((item, i) => (
                    <div key={i} className="border border-[#22c55e]/30 rounded-xl p-4 bg-[#22c55e]/5">
                      <p className="font-semibold text-sm text-[#01014C] mb-1">{item.headline}</p>
                      <p className="text-sm text-gray-700">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ⚠️ Flags / Watch-outs
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.flags.map((item, i) => (
                    <div key={i} className="border border-[#f97316]/30 rounded-xl p-4 bg-[#f97316]/5">
                      <p className="font-semibold text-sm text-[#01014C] mb-1">{item.headline}</p>
                      <p className="text-sm text-gray-700">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Scorecard */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                Quantitative Scorecard
              </h3>
              <div className="overflow-x-auto rounded-xl border border-[#36D1FF]/25">
                <table className="w-full text-sm">
                  <thead className="bg-[#36D1FF]/10 text-[#01014C]">
                    <tr>
                      <th className="text-left font-semibold px-4 py-3">Dimension</th>
                      <th className="text-left font-semibold px-4 py-3 w-[120px]">Score</th>
                      <th className="text-left font-semibold px-4 py-3">Rationale</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#36D1FF]/15">
                    {technicalAssessment.scorecard.map((row, idx) => (
                      <tr key={idx} className="bg-white">
                        <td className="px-4 py-3 font-medium text-[#01014C] align-top">{row.dimension}</td>
                        <td className="px-4 py-3 align-top"><ScorePill score={row.score} /></td>
                        <td className="px-4 py-3 text-gray-700 whitespace-pre-line align-top">{row.rationale}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* When to Hire / Cautions */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ✅ Hire if…
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.when_to_hire.map((item, i) => (
                    <div key={i} className="border border-[#36D1FF]/30 rounded-xl p-4 bg-[#36D1FF]/5 text-sm text-gray-700">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#01014C] mb-3 uppercase tracking-wide">
                  ⚠️ Be cautious or pass if…
                </h3>
                <div className="space-y-3">
                  {technicalAssessment.when_to_be_cautious.map((item, i) => (
                    <div key={i} className="border border-[#f59e0b]/30 rounded-xl p-4 bg-[#f59e0b]/5 text-sm text-gray-700">
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom Line */}
            <div className="border border-[#01014C]/20 rounded-xl p-5 bg-[#01014C]/5">
              <p className="text-sm font-bold text-[#01014C] mb-2 uppercase tracking-wide">
                🏁 Bottom Line
              </p>
              <p className="text-gray-700 text-sm leading-relaxed">
                {technicalAssessment.bottom_line}
              </p>
            </div>

          </div>
        </main>
      </div>

    </div>
  );
}